import * as Sentry from '@sentry/tanstackstart-react';
import { createMiddleware, json } from '@tanstack/react-start';
import { isAxiosError } from 'axios';

import { HttpStatus } from '@/lib/constants/http.status';
import {
  AppServerError,
  ExternalApiError,
  isValidationError,
  ValidationError,
} from '@/lib/errors';

import { loggingMiddleware } from './loggingMiddleware';

/**
 * Extracts the function identifier and path from the function ID.
 * Function id looks like this: src_routes_error-handling_tsx--test2_createServerFn_handler
 * @param fnId - The identifier of the server function.
 * @returns The function identifier and path.
 */
export const getServerFnInfo = (fnId: string) => {
  const parts = fnId.split('--');
  if (parts.length < 2) {
    return {
      functionId: 'Unknown Function',
      functionPath: 'Unknown Path',
    };
  }
  const [pathPart, fnIdPart] = parts;
  return {
    functionId:
      fnIdPart?.replace('_createServerFn_handler', '') ?? 'Unknown Function',
    functionPath:
      pathPart?.replace(/_tsx?$/, '').replaceAll('_', '/') ?? 'Unknown Path',
  };
};

type FunctionInfo = ReturnType<typeof getServerFnInfo>;

/**
 * Determines if the error should be reported to Sentry.
 *
 * @param error - The error to check.
 * @returns True if the error should be reported, false otherwise.
 */
const shouldReportError = (error: unknown) => {
  if (error instanceof ExternalApiError) {
    return (
      error.shouldReport || error.statusCode >= HttpStatus.InternalServerError
    );
  }
  if (error instanceof AppServerError) {
    return error.shouldReport;
  }
  if (isAxiosError(error)) {
    return (
      error.response?.status &&
      error.response.status >= HttpStatus.InternalServerError
    );
  }
  if (error instanceof Error) {
    return !isValidationError(error);
  }
  return true;
};

/**
 * Returns the error context to enrich the error report in Sentry.
 *
 * @param error - The error to get the context for.
 * @returns The error context.
 */
const getErrorContext = (error: unknown) => {
  if (error instanceof AppServerError) {
    return error.getReportContext();
  }

  return {};
};

const buildExternalApiErrorMessage = (
  error: ExternalApiError,
  functionInfo: FunctionInfo
) => {
  const requestData = error.data?.request as
    | {
        url?: string;
        method?: string;
      }
    | undefined;
  const requestedUrl = requestData?.url ?? 'Unknown URL';
  const requestedMethod = requestData?.method ?? 'Unknown Method';
  return `${error.message}: [${error.statusCode} ${requestedMethod} ${requestedUrl}] (FnId: ${functionInfo.functionId})`;
};

/**
 * Maps the error to a Sentry error.
 * Includes useful information for the error report in Sentry.
 *
 * @param error - The error to map.
 * @returns The mapped error.
 */
const mapErrorToSentry = (error: unknown, functionInfo: FunctionInfo) => {
  if (error instanceof ExternalApiError) {
    error.message = buildExternalApiErrorMessage(error, functionInfo);
    return error;
  }
  if (error instanceof AppServerError) {
    return error;
  }
  if (isAxiosError(error)) {
    return new ExternalApiError(
      error.message,
      error.response?.status ?? HttpStatus.InternalServerError,
      {
        request: {
          url: error.request.url,
          method: error.request.method,
          headers: Object.fromEntries(error.request.headers.entries()),
          data: error.request.data,
        },
        response: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        },
      }
    );
  }
  if (error instanceof Error) {
    if (isValidationError(error)) {
      return new ValidationError('Validation failed', {
        issues: error.message,
      });
    }

    if (error instanceof TypeError) {
      const code =
        (error.cause as { code: string } | undefined)?.code ?? 'UnknownError';
      const errors = (error.cause as { errors: string[] } | undefined)?.errors;
      return new AppServerError(
        `Type error: ${error.message} [${code}]`,
        HttpStatus.InternalServerError,
        {
          detail: errors,
        },
        true
      );
    }
  }
  return error;
};

/**
 * Reports the error to Sentry if it should be reported.
 *
 * @param error - The error to report.
 * @param functionInfo - The function information.
 */
export const maybeReportError = (
  error: unknown,
  functionInfo: FunctionInfo,
  requestId?: string,
  userContext?: {
    cognitoUser?: any;
    impersonation?: { isImpersonated: boolean; impersonatedUserId?: string };
  }
) => {
  if (shouldReportError(error)) {
    const mappedError = mapErrorToSentry(error, functionInfo);

    const sentryContext: any = {
      tags: {
        errorType: 'server-function',
        functionId: functionInfo.functionId,
        functionPath: functionInfo.functionPath,
        requestId,
      },
      extra: {
        ...getErrorContext(mappedError),
        serverSide: true,
        timestamp: new Date().toISOString(),
      },
      contexts: {},
    };

    if (userContext?.cognitoUser) {
      const user = userContext.cognitoUser;

      if (user.sub) sentryContext.tags.userCognitoId = user.sub;
      if (user.email) sentryContext.tags.userEmail = user.email;

      sentryContext.contexts.user_details = {
        cognitoId: user.sub,
        email: user.email,
        name: user.name,
        givenName: user.given_name,
        familyName: user.family_name,
      };
    }

    if (userContext?.impersonation?.isImpersonated) {
      sentryContext.tags.impersonated = true;
      sentryContext.tags.impersonatedUserId =
        userContext.impersonation.impersonatedUserId;
    }

    Sentry.captureException(mappedError, sentryContext);
  }
};

/**
 * Returns the appropriate error response based on the error type.
 */
export const getErrorResponse = (error: unknown): Response => {
  if (error instanceof AppServerError) {
    return json(
      {
        error: error.message,
        data: error.data,
      },
      {
        status: error.statusCode,
      }
    );
  }

  if (isAxiosError(error)) {
    return json(
      {
        error: error.message,
      },
      { status: error.response?.status ?? HttpStatus.InternalServerError }
    );
  }

  if (error instanceof Error) {
    if (isValidationError(error)) {
      const issues = JSON.parse(error.message);
      return json(
        {
          error: 'Validation failed',
          issues,
        },
        { status: HttpStatus.InvalidData }
      );
    }

    if (error instanceof TypeError) {
      const code =
        (error.cause as { code: string } | undefined)?.code ?? 'UnknownError';
      return json(
        {
          error: `Type error: ${error.message} [${code}]`,
        },
        { status: HttpStatus.InternalServerError }
      );
    }

    return json(
      {
        error: error.message,
      },
      { status: HttpStatus.InternalServerError }
    );
  }

  return json(
    {
      error: 'Internal server error',
    },
    { status: HttpStatus.InternalServerError }
  );
};

/**
 * Middleware that handles errors thrown by server functions.
 * It logs the error and reports it to the error reporting service.
 * It also returns a response with the error message.
 */
export const errorHandlingMiddleware = createMiddleware({
  type: 'function',
})
  .middleware([loggingMiddleware])
  .server(async ({ next, functionId, context }) => {
    const functionInfo = getServerFnInfo(functionId);
    try {
      return await next();
    } catch (error) {
      const userContext = {
        cognitoUser: (context as any).cognitoUser,
        impersonation: (context as any).impersonation,
      };

      maybeReportError(error, functionInfo, context.requestId, userContext);
      // The error response must be thrown to be handled by the framework.
      throw getErrorResponse(error);
    }
  });
